import { FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

// § 312k BGB cancellation flow - MUST work without login (identification via e-mail form
// field). Backend: LoginWithSengiPaymentService's public double-opt-out endpoints, reachable
// same-origin through UniversalProxy WITHOUT auth (dedicated no-auth route
// "/api/payment/v1/subscription/cancel-*" in routes.json). Deliberately plain fetch(), not
// fetchWithAuth/ApiOAuthClient - a logged-out visitor has no token.
//
// Flow: footer button "Verträge hier kündigen" -> this page (e-mail form) -> confirmation mail
// with token link back to this page (?token=...) -> explicit "Jetzt kündigen" button (§ 312k
// Bestätigungsbutton; also keeps mail-scanner prefetches from cancelling) -> confirmation view
// with date/time.
const CANCEL_API_BASE = '/api/payment/v1/subscription';

const RequestForm = () => {
    const { t } = useTranslation('cancelPage');
    const [email, setEmail] = useState('');
    const [resultMessage, setResultMessage] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await fetch(`${CANCEL_API_BASE}/cancel-request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await res.json().catch(() => null);
            // data?.message/data?.error kommen vom Backend (nicht lokalisiert, ausserhalb dieses
            // Repos) - nur die Frontend-eigenen Fallback-Texte werden hier uebersetzt.
            setResultMessage(
                data?.message ||
                (res.ok
                    ? t('request.successFallback')
                    : data?.error || t('request.failedFallback'))
            );
        } catch {
            setResultMessage(t('request.failedFallback'));
        } finally {
            setSubmitting(false);
        }
    };

    if (resultMessage) {
        return <div className="alert alert-info">{resultMessage}</div>;
    }

    return (
        <form onSubmit={submit}>
            <p>
                {t('request.intro')}
            </p>
            <div className="mb-3">
                <label htmlFor="cancel-email" className="form-label">{t('request.emailLabel')}</label>
                <input
                    id="cancel-email"
                    type="email"
                    className="form-control"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                />
            </div>
            <button type="submit" className="btn btn-danger" disabled={submitting}>
                {submitting ? t('request.submitting') : t('request.submit')}
            </button>
        </form>
    );
};

const ConfirmStep = ({ token }: { token: string }) => {
    const { t } = useTranslation('cancelPage');
    const [state, setState] = useState<'pending' | 'working' | 'done' | 'failed'>('pending');
    const [result, setResult] = useState<{ message?: string; confirmed_at?: string; period_end?: string; error?: string } | null>(null);

    const confirm = async () => {
        setState('working');
        try {
            const res = await fetch(`${CANCEL_API_BASE}/cancel-confirm?token=${encodeURIComponent(token)}`);
            const data = await res.json().catch(() => null);
            setResult(data);
            setState(res.ok && data?.success ? 'done' : 'failed');
        } catch {
            setState('failed');
        }
    };

    if (state === 'done') {
        return (
            <div className="alert alert-success">
                <h5>{t('confirm.doneTitle')}</h5>
                {/* § 312k: Bestätigung mit Datum/Uhrzeit */}
                {result?.confirmed_at && <p className="mb-1">{t('confirm.receivedAt', { date: result.confirmed_at })}</p>}
                {result?.period_end && <p className="mb-1">{t('confirm.periodEnd', { date: result.period_end })}</p>}
                <p className="mb-0">{t('confirm.emailNote')}</p>
            </div>
        );
    }
    if (state === 'failed') {
        return (
            <div className="alert alert-danger">
                {result?.error || t('confirm.failedFallback')}
            </div>
        );
    }
    return (
        <div>
            <p>
                {t('confirm.intro')}
            </p>
            <button type="button" className="btn btn-danger" onClick={confirm} disabled={state === 'working'}>
                {state === 'working' ? t('confirm.working') : t('confirm.submit')}
            </button>
        </div>
    );
};

export const CancelSubscriptionPage = () => {
    const { t } = useTranslation('cancelPage');
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    // document.title bleibt bewusst der deutsche Fixtext (§ 312k: dieselbe Beschriftung wie der
    // Footer-Link "Verträge hier kündigen", der dorthin fuehrt - vor dem ersten i18n-Tick hier
    // ohnehin noch keine Sprachauswahl bekannt).
    useEffect(() => { document.title = 'Verträge hier kündigen'; }, []);

    return (
        <div className="container py-5" style={{ maxWidth: 560 }}>
            <h2 className="mb-4">{t('title')}</h2>
            {token ? <ConfirmStep token={token} /> : <RequestForm />}
            <div className="mt-4 small">
                <Link to="/login">{t('footer.login')}</Link>
                <span className="mx-2 text-secondary">·</span>
                <Link to="/impressum">{t('footer.impressum')}</Link>
                <span className="mx-2 text-secondary">·</span>
                <Link to="/terms">{t('footer.terms')}</Link>
            </div>
        </div>
    );
};
